import React from 'react';
import { Button, Typography, useToast, Form } from '@crisellevaldez/speculo-ui';
import { SearchableSelect } from '../../../common/SearchableSelect';
import { Bell, BellOff } from 'lucide-react';
import { ConfirmationDialog } from '../../../common/dialogs/ConfirmationDialog';
import { useEmailContactTags } from '../../../../hooks/email/useEmailContactTags';
import {
  useRegisterBulkEAlert,
  useBulkUnsubscribeEAlert,
  useBulkUpdateEAlertFilters,
} from '../../../../hooks/client/email/useBulkEAlerts';
import { Organization } from '../../../../types/organization.types';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingDialog } from '../../../common/dialogs/LoadingDialog';

interface BulkEAlertSetupProps {
  selectedOrg?: Organization;
  onSuccess?: () => void;
  enabled?: boolean;
  onOpenAdvancedSetup?: (data: { tag: string }, isUpdate?: boolean) => void;
}

const eAlertSchema = z.object({
  tag: z.string().min(1, 'Tag is required'),
});

type EAlertFormData = z.infer<typeof eAlertSchema>;

export const BulkEAlertSetup: React.FC<BulkEAlertSetupProps> = ({
  selectedOrg,
  onSuccess: handleSuccess,
  enabled,
  onOpenAdvancedSetup,
}) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [showDisableConfirmation, setShowDisableConfirmation] = React.useState(false);
  const [showDisableAllConfirmation, setShowDisableAllConfirmation] = React.useState(false);

  // Get all available tags (paginated + searchable)
  const {
    data: tags,
    isLoading: tagsLoading,
    isError: tagsError,
    error: tagsErrorDetails,
    hasMore: tagsHasMore,
    loadMore: tagsLoadMore,
    setSearch: tagsSetSearch,
  } = useEmailContactTags(selectedOrg?.id, enabled);

  const { showToast } = useToast();
  const { mutateAsync: registerBulkEAlert, isPending: isSubmitting } = useRegisterBulkEAlert();
  const { mutate: bulkUnsubscribeEAlert, isPending: isUnsubscribing } = useBulkUnsubscribeEAlert();
  const { isPending: isUpdatingFilters } = useBulkUpdateEAlertFilters();

  const {
    handleSubmit,
    formState: { errors, isSubmitting: isFormSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<EAlertFormData>({
    resolver: zodResolver(eAlertSchema),
    defaultValues: {
      tag: '',
    },
  });

  const selectedTag = watch('tag');

  const onSubmit = (data: EAlertFormData) => {
    if (onOpenAdvancedSetup) {
      onOpenAdvancedSetup({
        tag: data.tag,
      });
    } else {
      setShowConfirmation(true);
    }
  };

  const handleConfirm = async () => {
    if (!selectedOrg) return;

    try {
      const formData = watch();

      await registerBulkEAlert({
        organization_id: selectedOrg.id,
        tag: formData.tag,
        interval: 'weekly',
      });

      showToast({
        message: 'eAlerts registered successfully for the selected tag',
        variant: 'success',
      });

      // Clear state after success
      setShowConfirmation(false);
      reset();
      handleSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : 'Failed to register eAlerts';
      showToast({ message, variant: 'error' });
      console.error('Error registering eAlerts:', error);
    }
  };

  const handleBulkDisableEAlerts = (withTag: boolean) => {
    if (!selectedOrg) return;

    const payload = withTag
      ? {
          organization_id: selectedOrg.id,
          tag: selectedTag,
        }
      : {
          organization_id: selectedOrg.id,
          ignore_tag_filter: true,
        };

    bulkUnsubscribeEAlert(payload, {
      onSuccess: (data) => {
        showToast({
          message: withTag
            ? `Successfully disabled eAlerts for ${data.contacts_unsubscribed || 0} contacts with tag "${selectedTag}"`
            : `Successfully disabled eAlerts for ${data.contacts_unsubscribed || 0} contacts`,
          variant: 'success',
        });

        // Clear state after success
        if (withTag) {
          setShowDisableConfirmation(false);
        } else {
          setShowDisableAllConfirmation(false);
        }
        reset();
        handleSuccess?.();
      },
      onError: (error) => {
        showToast({
          message: error instanceof Error ? error.message : 'Failed to disable eAlerts',
          variant: 'error',
        });
        console.error('Error disabling eAlerts:', error);
      },
    });
  };

  return (
    <div className="border-t border-gray-200 dark:border-white/10 pt-4 mt-4">
      <div className="space-y-4">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="space-y-4">
            <Form.Control required>
              <Form.Label>Select Tag</Form.Label>
              <SearchableSelect
                placeholder="Select a tag"
                options={tags?.map((tag) => ({ label: tag, value: tag })) || []}
                value={selectedTag}
                onChange={(val) => setValue('tag', val)}
                isLoading={tagsLoading}
                hasMore={tagsHasMore}
                onLoadMore={tagsLoadMore}
                onSearch={tagsSetSearch}
                searchPlaceholder="Search tags..."
                error={errors.tag?.message}
              />
              {tagsError && (
                <Typography variant="small" className="text-red-600 dark:text-red-400 mt-1">
                  {tagsErrorDetails instanceof Error
                    ? tagsErrorDetails.message
                    : 'Failed to load contact tags. Please try again.'}
                </Typography>
              )}
            </Form.Control>

            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || isFormSubmitting || isUpdatingFilters || tagsError}
                className="whitespace-nowrap"
              >
                Set Up Property eAlert
              </Button>
              <Button
                variant="outline"
                type="button"
                disabled={!selectedTag || isUpdatingFilters || isUnsubscribing}
                onClick={() => {
                  // Open advanced setup modal for updating filters
                  if (onOpenAdvancedSetup) {
                    onOpenAdvancedSetup({ tag: selectedTag }, true); // Pass true for update mode
                  }
                }}
                className="whitespace-nowrap"
              >
                <Bell className="w-4 h-4 mr-1" />
                Update Filters
              </Button>
              <Button
                variant="outline"
                type="button"
                disabled={!selectedTag || isUnsubscribing || isUpdatingFilters}
                onClick={() => setShowDisableConfirmation(true)}
                className="whitespace-nowrap !text-red-600 !border-red-300 hover:!bg-red-50 dark:hover:!bg-red-900/20"
              >
                <BellOff className="w-4 h-4 mr-1" />
                Disable by Tag
              </Button>
            </div>
          </div>
        </form>

        {/* Bulk Disable All eAlerts */}
        <div className="pt-4 border-t border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <Typography variant="p" className="font-semibold text-gray-700 dark:text-gray-300">
                Bulk Disable All eAlerts
              </Typography>
              <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                Disable eAlerts for all contacts in this organization
              </Typography>
            </div>
            <Button
              variant="outline"
              type="button"
              disabled={isUnsubscribing}
              onClick={() => setShowDisableAllConfirmation(true)}
              className="whitespace-nowrap !bg-red-600 !text-white !border-red-600"
            >
              <BellOff className="w-4 h-4 mr-1" />
              Disable All eAlerts
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirm}
        title="Set Up eAlerts"
        message={`Are you sure you want to set up property eAlerts for all contacts with the "${selectedTag}" tag?`}
        confirmLabel="Set Up"
        isLoading={isSubmitting}
        variant="warning"
      />

      <ConfirmationDialog
        isOpen={showDisableConfirmation}
        onClose={() => setShowDisableConfirmation(false)}
        onConfirm={() => handleBulkDisableEAlerts(true)}
        title="Disable eAlerts for Tag"
        message={`Are you sure you want to disable eAlerts for all contacts with the "${selectedTag}" tag in ${selectedOrg?.org_name}? This action cannot be undone.`}
        confirmLabel="Disable eAlerts"
        isLoading={isUnsubscribing}
        variant="danger"
      />

      <ConfirmationDialog
        isOpen={showDisableAllConfirmation}
        onClose={() => setShowDisableAllConfirmation(false)}
        onConfirm={() => handleBulkDisableEAlerts(false)}
        title="Disable All eAlerts"
        message={`Are you sure you want to disable eAlerts for ALL contacts in ${selectedOrg?.org_name}? This action cannot be undone.`}
        confirmLabel="Disable All eAlerts"
        isLoading={isUnsubscribing}
        variant="danger"
      />

      {/* Loading Dialog */}
      <LoadingDialog isOpen={isUnsubscribing} message="Processing bulk eAlert disable request..." />
    </div>
  );
};
